import { getDefaultGradient } from '@app/config-builder/common';
import { Box, Flex, parseColor, Switch, Text } from '@chakra-ui/react';
import { ColorItem } from './ColorItem';
import type { ColorPickerValueChangeDetails } from '@chakra-ui/react';


const GRADIENT_ANGLE = '285.3deg';
const STOP1_POS = '14.7%';
const STOP2_POS = '110.33%';

export function buildGradient(start: string, stop: string) {
  return `linear-gradient(${GRADIENT_ANGLE}, ${start} ${STOP1_POS}, ${stop} ${STOP2_POS})`;
}

function parseGradientColors(gradient: string): { start: string; stop: string } {
  const matches = gradient.match(/#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)/gi) ?? [];
  return { start: matches[0] ?? '#FFFFFF', stop: matches[1] ?? '#FFFFFF' };
}

export function safeParseColor(hex: string) {
  try {
    return parseColor(hex);
  } catch {
    return undefined;
  }
}

type Props = {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  reset: () => void;
};

export const GradientColorItem = ({ label, value, onChange, reset }: Props) => {
  const isGradient = typeof value === 'string' && value.trimStart().startsWith('linear-gradient');

  const handleToggle = (toGradient: boolean) => {
    if (toGradient) {
      const solidHex =
        typeof value === 'string' && !value.trimStart().startsWith('linear-gradient') ? value : '#FFFFFF';
      onChange(getDefaultGradient(solidHex));
    } else {
      const { start } = parseGradientColors(value ?? '');
      onChange(start);
    }
  };

  const gradientColors = isGradient ? parseGradientColors(value!) : { start: '#FFFFFF', stop: '#FFFFFF' };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="2">
        <Text textStyle="sm" fontWeight="medium">
          {label}
        </Text>
        <Switch.Root size="sm" checked={isGradient} onCheckedChange={({ checked }) => handleToggle(checked)}>
          <Switch.HiddenInput />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{isGradient ? 'Gradient' : 'Solid'}</Switch.Label>
        </Switch.Root>
      </Flex>

      {/*Labels and values are inverted here, this is not a mistake*/}
      {isGradient ? (
        <Flex direction="column" gap="3" pl="3" borderLeftWidth="2px" borderColor="border.subtle">
          <ColorItem
            label="Start"
            value={safeParseColor(gradientColors.stop)}
            reset={reset}
            onChange={(details: ColorPickerValueChangeDetails) => {
              const { start } = parseGradientColors(value!);
              onChange(buildGradient(start, details.value.toString('hex')));
            }}
          />
          <ColorItem
            label="Stop"
            value={safeParseColor(gradientColors.start)}
            reset={reset}
            onChange={(details: ColorPickerValueChangeDetails) => {
              const { stop } = parseGradientColors(value!);
              onChange(buildGradient(details.value.toString('hex'), stop));
            }}
          />
        </Flex>
      ) : (
        <ColorItem
          label=""
          value={value ? safeParseColor(value.split(' ')[0]) : undefined}
          reset={reset}
          onChange={(details: ColorPickerValueChangeDetails) => onChange(details.value.toString('hex'))}
        />
      )}
    </Box>
  );
};
